import { createPaymentEventKey } from './orders-page-shared.utils';

/** Context passed by order list components for shared payment event handling */
export interface PaymentEventContext {
  processedPayments: Set<string>;
  messageService: { add(msg: { severity?: string; summary?: string; detail?: string; life?: number; [key: string]: any }): void };
  closePaymentLinkModal?: () => void;
  closeAddOrderModal: () => void;
  /** When set, called instead of closeAddOrderModal so the add-order modal can show its own success modal. */
  notifyAddOrderPaymentLinkPaid?: () => void;
  /** Broadcast to OrdersService so the notification modal can finish payment-link flow when SSE fires payment success. */
  emitGlobalPaymentSucceeded?: (orderId: string) => void;
  loadOrders: () => void;
  showOrderDetailsModal?: boolean;
  orderDetailsModalComponent?: { refreshPaymentTab(): void };
  selectedOrder?: { id: string; orderId: string } | null;
  logger?: { trackPayment(message: string, meta?: Record<string, any>): void };
}

/**
 * Shared payment success handler. Used by admin-orders, staff-orders, orders.
 */
export function handlePaymentSuccessFromContext(
  ctx: PaymentEventContext,
  eventName: string,
  data: any
): void {
  const status = data?.status || 'any';
  if (status !== 'succeeded') return;

  const paymentKey = createPaymentEventKey(eventName, data);
  if (ctx.processedPayments.has(paymentKey)) {
    ctx.logger?.trackPayment('Duplicate payment success event suppressed', {
      sseEvent: eventName,
      paymentKey,
      data
    });
    return;
  }

  ctx.logger?.trackPayment('Payment succeeded via SSE', {
    sseEvent: eventName,
    paymentKey,
    orderId: data?.order_id || data?.orderId,
    paymentIntentId: data?.payment_intent_id || data?.payment_intent,
    status
  });

  ctx.processedPayments.add(paymentKey);
  setTimeout(() => ctx.processedPayments.delete(paymentKey), 5 * 60 * 1000);

  const orderId = data?.order_id || data?.orderId || data?.order?.id;
  if (orderId) {
    ctx.emitGlobalPaymentSucceeded?.(String(orderId));
  }

  // If the add-order modal is open with a payment link, delegate to it so it can
  // show its own success modal instead of a bare toast.
  if (ctx.notifyAddOrderPaymentLinkPaid) {
    ctx.notifyAddOrderPaymentLinkPaid();
  } else {
    if (ctx.closePaymentLinkModal) ctx.closePaymentLinkModal();
    ctx.closeAddOrderModal();
  }

  // Always show the toast so the user gets immediate feedback.
  ctx.messageService.add({
    severity: 'success',
    summary: 'Payment Successful',
    detail: 'Payment completed successfully. Order has been scheduled.',
    life: 4000
  });

  if (
    ctx.showOrderDetailsModal &&
    ctx.orderDetailsModalComponent &&
    orderId &&
    ctx.selectedOrder &&
    (ctx.selectedOrder.id === orderId || ctx.selectedOrder.orderId === orderId)
  ) {
    // Success modal is shown by payment-tab; list handlers only refresh the tab.
    setTimeout(() => {
      ctx.orderDetailsModalComponent!.refreshPaymentTab();
    }, 500);
  }

  ctx.loadOrders();
}

/**
 * Shared payment failure handler. Used by admin-orders, staff-orders, orders.
 */
export function handlePaymentFailedFromContext(
  ctx: PaymentEventContext,
  eventName: string,
  data: any
): void {
  const status = data?.status || 'any';
  if (status !== 'failed') return;

  const paymentKey = createPaymentEventKey(eventName, data);
  if (ctx.processedPayments.has(paymentKey)) {
    ctx.logger?.trackPayment('Duplicate payment failure event suppressed', {
      sseEvent: eventName,
      paymentKey,
      data
    });
    return;
  }

  ctx.logger?.trackPayment('Payment failed via SSE', {
    sseEvent: eventName,
    paymentKey,
    orderId: data?.order_id || data?.orderId,
    paymentIntentId: data?.payment_intent_id || data?.payment_intent,
    errorMessage: data?.message || data?.error_message,
    status
  });

  ctx.processedPayments.add(paymentKey);
  setTimeout(() => ctx.processedPayments.delete(paymentKey), 5 * 60 * 1000);

  const errorMessage =
    data?.message || data?.error_message || 'Payment could not be processed. Please try again.';
  ctx.messageService.add({
    severity: 'error',
    summary: 'Payment Failed',
    detail: errorMessage,
    life: 5000
  });

  ctx.loadOrders();
}
